/**
 * The question about cookies on the static pages, and the counter it allows.
 *
 * The banner is in the page (site/index.html, site/privacy.html), hidden; it is shown when
 * the page has a counter (the server names it: src/shared/lib/analytics/config.ts) and the
 * visitor has not answered yet, here or in the application: the answer is a cookie of the
 * whole domain (src/shared/lib/analytics/consent.ts), as the language is.
 */
import { Analytics } from '../src/shared/lib/analytics/index';
import { forgetConsent, readConsent, rememberConsent, type Consent } from '../src/shared/lib/analytics/consent';

/** The domain a cookie is for: the whole installation, which a bare host name or an address cannot name. */
function cookieDomain(): string | null {
  const host = location.hostname;
  return host.includes('.') && !/^[\d.]+$/.test(host) && !host.includes(':') ? host : null;
}

export function askConsent(analytics: Analytics = new Analytics()): void {
  // Already agreed (here or in the application): counted at once.
  if (readConsent() === 'all') analytics.page();
  const banner = document.querySelector<HTMLElement>('[data-consent]');
  if (banner && analytics.configured && readConsent() === null) {
    banner.hidden = false;
    const answer = (consent: Consent) => {
      rememberConsent(consent, cookieDomain());
      banner.hidden = true;
      if (consent === 'all') analytics.page();
    };
    banner.querySelector('[data-consent-accept]')?.addEventListener('click', () => answer('all'));
    banner.querySelector('[data-consent-necessary]')?.addEventListener('click', () => answer('necessary'));
  }
  // The policy page lets the visitor answer again: the answer is forgotten, and asked anew.
  for (const reset of document.querySelectorAll<HTMLElement>('[data-consent-reset]')) {
    reset.addEventListener('click', () => {
      forgetConsent(cookieDomain());
      location.reload();
    });
  }
}
